import { type ReactNode, type RefObject, useRef } from 'react';
import { Header, Text, Tooltip, type TooltipProps, VStack } from '@direct-frontend/components';
import { useToggle } from '@direct-frontend/stdlib';
import { Drawer, DrawerBottomFixed, DrawerButton, DrawerHeader } from '@/shared/components/Drawer';
import { useIsDesktop } from '@/shared/lib/hooks';
import styles from './LoyaltyTooltip.module.css';

interface TooltipPayload<T extends Element = HTMLElement> {
    openTooltip: VoidFunction;
    ref: RefObject<T>;
}

interface LoyaltyTooltipProps<T extends Element = HTMLElement> {
    children: (props: TooltipPayload<T>) => ReactNode;
    title?: ReactNode;
    subtitle?: ReactNode;
    tooltipProps?: Omit<TooltipProps, 'triggerRef'>;
}

export const LoyaltyTooltip = <T extends Element = HTMLElement>(props: LoyaltyTooltipProps<T>) => {
    const { children, title, subtitle, tooltipProps = {} } = props;

    const { isDesktop } = useIsDesktop();
    const { isOpen, setTruthy: openTooltip, setFalsy: closeTooltip } = useToggle();

    const ref = useRef<T>(null);

    if (isDesktop) {
        return (
            <>
                {children({ openTooltip, ref })}

                <Tooltip
                    title={
                        <VStack gap="4" className={styles.infoTooltip}>
                            {title && (
                                <Header level="h4" className={styles.infoTooltipTitle}>
                                    {title}
                                </Header>
                            )}

                            {subtitle && (
                                <Text role="supplementary" className={styles.infoTooltipText}>
                                    {subtitle}
                                </Text>
                            )}
                        </VStack>
                    }
                    triggerRef={ref}
                    isOpen={isOpen}
                    onClose={closeTooltip}
                    className={styles.infoTooltip}
                    {...tooltipProps}
                />
            </>
        );
    }

    return (
        <>
            {children({ openTooltip, ref })}

            <Drawer isOpen={isOpen} onClose={closeTooltip}>
                <DrawerHeader className={styles.infoDrawer} title={title} subtitle={subtitle} />

                <DrawerBottomFixed>
                    <DrawerButton type="secondary" onClick={closeTooltip}>
                        Хорошо
                    </DrawerButton>
                </DrawerBottomFixed>
            </Drawer>
        </>
    );
};
